import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import AvatarCircle from "app/components/AvatarCircle";
import { Input } from "app/shadcnComponents/ui/input";
import { useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { EmailRecipientPreviewUser } from "../types";

interface Props<T extends EmailRecipientPreviewUser> {
  title: string;
  users: T[];
  emptyState?: React.ReactNode;
  renderRowAction: (user: T) => React.ReactNode;
}

const getUserDisplayName = (user: EmailRecipientPreviewUser) =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

const RecipientSection = <T extends EmailRecipientPreviewUser>({
  title,
  users,
  emptyState,
  renderRowAction,
}: Props<T>) => {
  const [search, setSearch] = useState("");
  const usersById = useAppSelector(selectAllCompanyUsersById);

  const filteredUsers = users.filter((user) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const jobTitle = usersById[user.userAccountId]?.jobTitle ?? "";
    return (
      getUserDisplayName(user).toLowerCase().includes(searchLower) ||
      jobTitle.toLowerCase().includes(searchLower)
    );
  });

  const getListContent = () => {
    if (!users.length) {
      return (
        emptyState ?? (
          <p className="p-2 text-xs text-charcoal-80">No one to show here.</p>
        )
      );
    }

    if (!filteredUsers.length) {
      return (
        <p className="p-2 text-xs text-charcoal-80">
          No matches for “{search}”.
        </p>
      );
    }

    return filteredUsers.map((user) => {
      const jobTitle = usersById[user.userAccountId]?.jobTitle;
      return (
        <div
          className="flex items-center justify-between gap-3 p-2"
          key={user.userAccountId}
        >
          <div className="flex min-w-0 items-center gap-3">
            <AvatarCircle
              userAccountId={user.userAccountId}
              name={getUserDisplayName(user)}
              size="small"
            />
            <div className="min-w-0">
              <p className="truncate">{getUserDisplayName(user)}</p>
              {jobTitle ? (
                <p className="truncate text-xs text-charcoal-80">{jobTitle}</p>
              ) : null}
            </div>
          </div>
          {renderRowAction(user)}
        </div>
      );
    });
  };

  return (
    <div>
      <p className="mb-2 navy-text">{title}</p>
      <div className="relative mb-2">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-sm text-charcoal-60"
        />
        <Input
          type="text"
          aria-label={`Search ${title}`}
          className="h-[38px] border-charcoal-20 pl-[30px] text-sm focus-visible:border-sapphire-100 focus-visible:ring-1 focus-visible:ring-sapphire-60 focus-visible:ring-offset-0"
          placeholder="Search by name or job title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="flex max-h-[220px] flex-col gap-1 overflow-y-auto rounded-lg bg-charcoal-05 p-1">
        {getListContent()}
      </div>
    </div>
  );
};

export default RecipientSection;
